import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { MemberClassHistorySchedule, YearSchedule } from '../../services/useCenterQueryService';
import { Col, Row } from '../../lib/utils';
import { PeriodNavigatorType } from '../Date/PeriodNavigator';
import MemberClassDayCard, { MemberClassEntry } from './MemberClassDayCard';

type Props = {
    type: PeriodNavigatorType;
    currentDate: string;
    monthlyData?: MemberClassHistorySchedule[] | MemberClassEntry[];
    yearlyData?: YearSchedule;
    totalData?: YearSchedule[];
    onClickRow?: (row: any) => void;
    coachMap?: Record<string, string>;
};

// 중첩 구조(YearSchedule)에서 startTime을 가진 스케줄 객체를 재귀적으로 추출
const flattenToSchedules = (obj: any): MemberClassEntry[] => {
    if (!obj) return [];
    if (Array.isArray(obj)) {
        return obj.flatMap((item) => flattenToSchedules(item));
    }
    if (typeof obj === 'object') {
        if (obj.startTime) return [obj as MemberClassEntry];
        return Object.values(obj).flatMap((v) => flattenToSchedules(v));
    }
    return [];
};

// 날짜(YYYY-MM-DD)별로 그룹화, 최신 날짜 → 오래된 날짜 순
const groupByDay = (items: MemberClassEntry[]): Array<{ date: string; classes: MemberClassEntry[] }> => {
    const map = new Map<string, MemberClassEntry[]>();
    items.forEach((s) => {
        if (!s?.startTime) return;
        const key = dayjs(s.startTime).format('YYYY-MM-DD');
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(s);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
        .map(([date, classes]) => ({
            date,
            classes: classes.sort((a, b) => (a.startTime < b.startTime ? 1 : -1)),
        }));
};

// 월(1-12)별로 그룹화
const groupByMonth = (items: MemberClassEntry[]): Array<{ month: number; classes: MemberClassEntry[] }> => {
    const map = new Map<number, MemberClassEntry[]>();
    items.forEach((s) => {
        if (!s?.startTime) return;
        const m = dayjs(s.startTime).month() + 1;
        if (!map.has(m)) map.set(m, []);
        map.get(m)!.push(s);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([month, classes]) => ({ month, classes }));
};

// 연도별로 그룹화
const groupByYear = (items: MemberClassEntry[]): Array<{ year: number; classes: MemberClassEntry[] }> => {
    const map = new Map<number, MemberClassEntry[]>();
    items.forEach((s) => {
        if (!s?.startTime) return;
        const y = dayjs(s.startTime).year();
        if (!map.has(y)) map.set(y, []);
        map.get(y)!.push(s);
    });
    return Array.from(map.entries())
        .sort(([a], [b]) => b - a)
        .map(([year, classes]) => ({ year, classes }));
};

const EmptyState = () => (
    <Col
        style={{
            width: '100%',
            padding: '60px 0',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            backgroundColor: '#FFFFFF',
            border: '1px dashed #E2E8F0',
            borderRadius: 12,
        }}
    >
        <span style={{ fontSize: 32 }}>📭</span>
        <span style={{ fontSize: 14, color: '#9CA3AF', fontWeight: 500 }}>조회 결과가 없어요</span>
    </Col>
);

// 접기/펼치기 토글 버튼
const ToggleIndicator = ({ isOpen }: { isOpen: boolean }) => (
    <span
        style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#6B7280',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
        }}
    >
        {isOpen ? '접기' : '펼치기'}
        <span
            style={{
                fontSize: 9,
                transform: isOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
            }}
        >
            ▼
        </span>
    </span>
);

const MemberClassHistoryComponentsGrid = ({
    type,
    monthlyData,
    yearlyData,
    totalData,
    currentDate,
    onClickRow,
    coachMap,
}: Props) => {
    const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});
    const toggleMonth = (key: string) =>
        setOpenMonths((prev) => ({ ...prev, [key]: !prev[key] }));

    // ━━━ 이번 달 ━━━
    if (type === 'thisMonth') {
        const groups = groupByDay((monthlyData as MemberClassEntry[]) ?? []);
        if (groups.length === 0) return <Col style={{ width: '100%', marginBottom: 48 }}><EmptyState /></Col>;
        return (
            <Col style={{ width: '100%', gap: 10, marginBottom: 48 }}>
                {groups.map((g, idx) => (
                    <MemberClassDayCard
                        key={g.date}
                        date={g.date}
                        classes={g.classes}
                        accentIdx={idx}
                        coachMap={coachMap}
                        onClickRow={onClickRow}
                    />
                ))}
            </Col>
        );
    }

    // ━━━ 이번 연도 ━━━
    if (type === 'thisYear') {
        const all = flattenToSchedules(yearlyData);
        if (all.length === 0) return <Col style={{ width: '100%', marginBottom: 48 }}><EmptyState /></Col>;
        const monthGroups = groupByMonth(all);
        return (
            <Col style={{ width: '100%', gap: 12, marginBottom: 48 }}>
                {monthGroups.map(({ month, classes }) => {
                    const key = `year-${month}`;
                    const isOpen = !!openMonths[key];
                    const days = groupByDay(classes);
                    return (
                        <Col key={key} style={{ gap: 8 }}>
                            <Row
                                onClick={() => toggleMonth(key)}
                                style={{
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '10px 16px',
                                    backgroundColor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: 10,
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{ fontSize: 16, fontWeight: 800, color: '#2563EB' }}>
                                    {month}월
                                </span>
                                <span style={{ fontSize: 13, color: '#475569' }}>
                                    총 <b style={{ color: '#2563EB', fontSize: 14 }}>{classes.length}</b>회 진행
                                </span>
                                <div style={{ flex: 1 }} />
                                <ToggleIndicator isOpen={isOpen} />
                            </Row>
                            {isOpen && (
                                <Col style={{ gap: 8 }}>
                                    {days.map((g, idx) => (
                                        <MemberClassDayCard
                                            key={g.date}
                                            date={g.date}
                                            classes={g.classes}
                                            accentIdx={idx}
                                            coachMap={coachMap}
                                            onClickRow={onClickRow}
                                        />
                                    ))}
                                </Col>
                            )}
                        </Col>
                    );
                })}
            </Col>
        );
    }

    // ━━━ 전체 ━━━
    if (type === 'all') {
        const all = flattenToSchedules(totalData);
        if (all.length === 0) return <Col style={{ width: '100%', marginBottom: 48 }}><EmptyState /></Col>;
        const yearGroups = groupByYear(all);
        return (
            <Col style={{ width: '100%', gap: 16, marginBottom: 48 }}>
                {yearGroups.map(({ year, classes: yearClasses }) => {
                    const months = groupByMonth(yearClasses);
                    return (
                        <Col key={year} style={{ gap: 10 }}>
                            <Row
                                style={{
                                    alignItems: 'baseline',
                                    gap: 10,
                                    padding: '4px 4px 6px',
                                    borderBottom: '2px solid #E5E7EB',
                                }}
                            >
                                <span style={{ fontSize: 18, fontWeight: 800, color: '#111827', letterSpacing: -0.3 }}>
                                    {year}년
                                </span>
                                <span
                                    style={{
                                        padding: '2px 10px',
                                        borderRadius: 999,
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        border: '1px solid #BFDBFE',
                                    }}
                                >
                                    총 {yearClasses.length}회
                                </span>
                            </Row>
                            <Col style={{ gap: 10 }}>
                                {months.map(({ month, classes }) => {
                                    const key = `all-${year}-${month}`;
                                    const isOpen = !!openMonths[key];
                                    const days = groupByDay(classes);
                                    return (
                                        <Col key={key} style={{ gap: 8 }}>
                                            <Row
                                                onClick={() => toggleMonth(key)}
                                                style={{
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    padding: '10px 16px',
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: 10,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <span style={{ fontSize: 16, fontWeight: 800, color: '#2563EB' }}>
                                                    {year}년 {month}월
                                                </span>
                                                <span style={{ fontSize: 13, color: '#475569' }}>
                                                    총{' '}
                                                    <b style={{ color: '#2563EB', fontSize: 14 }}>{classes.length}</b>회 진행
                                                </span>
                                                <div style={{ flex: 1 }} />
                                                <ToggleIndicator isOpen={isOpen} />
                                            </Row>
                                            {isOpen && (
                                                <Col style={{ gap: 8 }}>
                                                    {days.map((g, idx) => (
                                                        <MemberClassDayCard
                                                            key={g.date}
                                                            date={g.date}
                                                            classes={g.classes}
                                                            accentIdx={idx}
                                                            coachMap={coachMap}
                                                            onClickRow={onClickRow}
                                                        />
                                                    ))}
                                                </Col>
                                            )}
                                        </Col>
                                    );
                                })}
                            </Col>
                        </Col>
                    );
                })}
            </Col>
        );
    }

    return null;
};

export default MemberClassHistoryComponentsGrid;
